import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    PopupParagraph,
    PopupParagraphSmall,
    BoldSpan,
    BoxWrapper,
    BoxTitle,
    LinkWrapper,
} from 'src/domains/players/state/accountState/DepositLimitsUpdatePopups/DepositLimitUpdatePopup/DepositLimitUpdatePopup.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';

export type PopupVariant = 'primary' | 'secondary';

interface PopupContentProps {
    variant: PopupVariant;
    accountHelperMail: string;
}

const PrimaryContent = observer(
    'PrimaryContent',
    (): JSX.Element => (
        <>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.primary.intro'
                    defaultText="We're updating our deposit limits to help you stay in control of your gambling."
                />
            </PopupParagraph>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.primary.rolling-explanation-prefix'
                    defaultText='Moving forward, your chosen Deposit Limits will be calculated using'
                />
                <BoldSpan>
                    <I18n
                        langKey='deposit-limit-update.popup.rolling-net-deposit-limits'
                        defaultText=' Rolling Net Deposit Limits'
                    />
                </BoldSpan>
                <I18n
                    langKey='deposit-limit-update.popup.primary.rolling-explanation-suffix'
                    defaultText=". This means you'll be able to deposit your set amount plus any withdrawals you've made, and it will be calculated over rolling periods instead of calendar-based periods."
                />
            </PopupParagraph>
            <BoxWrapper>
                <BoxTitle>
                    <I18n
                        langKey='deposit-limit-update.popup.box-title'
                        defaultText='Example'
                    />
                </BoxTitle>
                <PopupParagraphSmall>
                    <I18n
                        langKey='deposit-limit-update.popup.primary.box-line-1'
                        defaultText='Set deposit limit: £100 a week'
                    />
                </PopupParagraphSmall>
                <PopupParagraphSmall>
                    <I18n
                        langKey='deposit-limit-update.popup.primary.box-line-2'
                        defaultText='Deposit £60 → £40 remaining of your deposit limit.'
                    />
                </PopupParagraphSmall>
                <PopupParagraphSmall>
                    <I18n
                        langKey='deposit-limit-update.popup.primary.box-line-3'
                        defaultText='Withdraw £10 → £50 remaining of your deposit limit.'
                    />
                </PopupParagraphSmall>
            </BoxWrapper>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.primary.action-needed'
                    defaultText="No action is needed from you. Your current limit amount stays the same. If you haven't yet set a Deposit Limit, we advise you to consider this option."
                />
            </PopupParagraph>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.primary.faq-info'
                    defaultText='For more information, please visit our FAQ and Responsible Gambling pages.'
                />
            </PopupParagraph>
        </>
    )
);

interface SecondaryContentProps {
    accountHelperMail: string;
}

const SecondaryContent = observer(
    'SecondaryContent',
    ({ accountHelperMail }: SecondaryContentProps): JSX.Element => (
        <>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.secondary.intro'
                    defaultText="We're just letting you know that we've updated the way our deposit limits work."
                />
            </PopupParagraph>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.secondary.rolling-explanation-prefix'
                    defaultText='From now on, your chosen deposit limit will be a'
                />
                <BoldSpan>
                    <I18n
                        langKey='deposit-limit-update.popup.rolling-net-deposit-limits'
                        defaultText=' Rolling Net Deposit Limits'
                    />
                </BoldSpan>
                <I18n
                    langKey='deposit-limit-update.popup.secondary.rolling-explanation-suffix'
                    defaultText=". This means you'll be able to deposit your set amount, plus any withdrawals you've made, over a rolling period (instead of a fixed calendar period)."
                />
            </PopupParagraph>
            <BoxWrapper>
                <BoxTitle>
                    <I18n
                        langKey='deposit-limit-update.popup.box-title'
                        defaultText='Example'
                    />
                </BoxTitle>
                <PopupParagraphSmall>
                    <I18n
                        langKey='deposit-limit-update.popup.secondary.box-line-1'
                        defaultText='You set a deposit limit of £100 per week.'
                    />
                </PopupParagraphSmall>
                <PopupParagraphSmall>
                    <I18n
                        langKey='deposit-limit-update.popup.secondary.box-line-2'
                        defaultText='You deposit £60 which leaves you £40 to deposit for the next 7 days.'
                    />
                </PopupParagraphSmall>
                <PopupParagraphSmall>
                    <I18n
                        langKey='deposit-limit-update.popup.secondary.box-line-3'
                        defaultText="If you withdraw £10, you'll now be able to deposit £50 in the next 7 days."
                    />
                </PopupParagraphSmall>
            </BoxWrapper>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.secondary.action-needed'
                    defaultText="The good news is that you don't need to do anything. Any limits you've already set will stay at the same amount. If you haven't set a deposit limit yet, you can do so using the Responsible Gambling section of your account."
                />
            </PopupParagraph>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.contact-intro'
                    defaultText='If you have any questions, please visit our FAQ page or drop our team an email at '
                />
                <LinkWrapper to={`mailto:${accountHelperMail}`}>
                    <I18n
                        langKey='account.deposit.info-page.email'
                        defaultText='{accountHelperMail}'
                        params={{ accountHelperMail }}
                    />
                </LinkWrapper>
                .
            </PopupParagraph>
            <PopupParagraph>
                <I18n
                    langKey='deposit-limit-update.popup.secondary.thanks'
                    defaultText='Thanks for reading!'
                />
            </PopupParagraph>
        </>
    )
);

export const PopupContent = observer(
    'PopupContent',
    ({ variant, accountHelperMail }: PopupContentProps): JSX.Element => {
        if (variant === 'secondary') {
            return <SecondaryContent accountHelperMail={accountHelperMail} />;
        }
        return <PrimaryContent />;
    }
);
